import { useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type * as API from "@shared-core/data/api-types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Badge } from "../app/components/primitives/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../app/components/primitives/alert-dialog";
import { Plus, Pencil, Archive, Check, X } from "lucide-react";

export function SettingsTicketCategoriesPage() {
  const dataAdapter = useDataAdapter();
  const [categories, setCategories] = useState<API.TicketCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRetired, setShowRetired] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [retireId, setRetireId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadCategories = async () => {
    setLoading(true);
    const result = await dataAdapter.listTicketCategories(!showRetired);
    setCategories(result);
    setLoading(false);
  };

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRetired]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setIsSaving(true);
    try {
      await dataAdapter.createTicketCategory({ name });
      setNewName("");
      setIsCreating(false);
      await loadCategories();
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEdit = (category: API.TicketCategory) => {
    setEditingId(category.id);
    setEditingName(category.name);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const name = editingName.trim();
    if (!name) return;
    setIsSaving(true);
    try {
      await dataAdapter.updateTicketCategory(editingId, { name });
      setEditingId(null);
      await loadCategories();
    } finally {
      setIsSaving(false);
    }
  };

  const handleRetire = async () => {
    if (!retireId) return;
    await dataAdapter.retireTicketCategory(retireId);
    setRetireId(null);
    await loadCategories();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl mb-2">Ticket Categories</h1>
          <p className="text-sm text-muted-foreground">
            The category taxonomy tickets are classified against. Retiring a
            category keeps it on existing tickets but removes it from the picker
            for new ones.
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="size-4 mr-2" />
          New Category
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowRetired(!showRetired)}
          className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
            showRetired
              ? "bg-muted text-foreground font-medium"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {showRetired ? "Showing retired" : "Show retired"}
        </button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isCreating && (
              <TableRow>
                <TableCell>
                  <Input
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Category name"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreate();
                      if (e.key === "Escape") {
                        setIsCreating(false);
                        setNewName("");
                      }
                    }}
                  />
                </TableCell>
                <TableCell colSpan={2}>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleCreate}
                      disabled={!newName.trim() || isSaving}
                    >
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsCreating(false);
                        setNewName("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : categories.length === 0 && !isCreating ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-center py-8 text-muted-foreground"
                >
                  No categories yet
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell className="font-medium">
                    {editingId === category.id ? (
                      <Input
                        autoFocus
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEdit();
                          if (e.key === "Escape") setEditingId(null);
                        }}
                      />
                    ) : (
                      category.name
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={category.is_active ? "default" : "secondary"}
                    >
                      {category.is_active ? "Active" : "Retired"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {editingId === category.id ? (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={!editingName.trim() || isSaving}
                        >
                          <Check className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingId(null)}
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        {category.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleStartEdit(category)}
                          >
                            <Pencil className="size-4" />
                          </Button>
                        )}
                        {category.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRetireId(category.id)}
                          >
                            <Archive className="size-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!retireId} onOpenChange={() => setRetireId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire this category?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing tickets keep this category. It's removed from the picker
              for new tickets and can't be reactivated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRetire}>Retire</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
